import { useCallback, useMemo } from 'react';
import {
  ACCOUNT_LAYOUTS,
  FLOW_LAYOUT_FALLBACK_ID,
  FLOW_LAYOUT_TABLE_FALLBACK_ID,
  HOME_LAYOUTS,
  LIST_LAYOUTS,
  UI_VIEW_FLOW_LAYOUTS,
  listFlowLayoutsFor,
  flowLayoutSourceLabel,
  resolveFlowLayout,
  FlowAccountLayoutIdSchema,
  type FlowAccountLayoutId,
  type FlowHomeLayoutId,
  type FlowLayoutSlot,
  type FlowLayoutSource,
  type FlowLayoutTable,
  type FlowListLayoutId,
  type FlowListLayoutSpec,
} from '@shared/flow/flow-layouts';
import type { FlowRole } from '@shared/flow/role';
import {
  flowLayoutPinWriteInFlight,
  setWorkingFlowLayout,
  useFlowLayoutWorkingStore,
  useWorkingFlowLayout,
  workingFlowLayoutVersion,
} from '../components/flow/flow-layouts/flow-layout-working-store';
import { useLayoutPrefs } from './useLayoutPrefs';
import { useRoleView } from './useRoleView';

/**
 * Flow layout resolver hooks: which layout a surface shows, and the rep's
 * pick / set-as-default / reset for it. Everything is derived every render
 * from the User Role (useRoleView), the rep's saved choices (useLayoutPrefs)
 * and the session-only working ("Modified") pick. No state of their own.
 *
 * Account, Home and every table behave the same: a pick is a working pick
 * until "Set as default layout" (or, on a table with saved views, a view Save)
 * commits it; Reset is two-step (discard the working pick, then clear the
 * saved default).
 */

export interface FlowLayoutResult<Id extends string, Spec> {
  id: Id;
  source: FlowLayoutSource;
  /** The source badge text for the menu's checked row. */
  sourceLabel: string;
  spec: Spec;
  /** The layouts this surface offers, in menu order. */
  options: Spec[];
  /** True once /api/me and the view preferences have loaded (`useRoleView().isLoaded`); until then the surface must wait, because the saved choice and the User Role are not known yet. */
  isLoaded: boolean;
  /** True when the rep has a choice of their own that reset would clear: a working pick or a saved default (`working !== null || saved !== null`). */
  hasChoice: boolean;
  /** What shows without a working pick (the saved default, view pin, UI View default or fallback). A pick equal to it is not Modified. */
  baselineId: Id;
  /** True when the shown layout is the unsaved working pick (source `'working'`): the menu reads "Modified". */
  isModified: boolean;
  /** A working pick; picking the layout that shows without one clears it instead. */
  pick: (id: Id) => void;
  /**
   * Writes the working pick as the rep's saved default for this surface.
   * Resolves `true` once the server holds it (the working pick is then
   * cleared, unless the rep picked again meanwhile); `false` with nothing to
   * set, or when the write failed (already rolled back
   * and toasted; the pick stays Modified).
   */
  setAsDefault: () => Promise<boolean>;
  /** Two-step: discards a working pick if there is one, else clears the saved default. */
  reset: () => void;
}

const noop = () => {};

type AccountSpec = (typeof ACCOUNT_LAYOUTS)[FlowAccountLayoutId];
type HomeSpec = (typeof HOME_LAYOUTS)[FlowHomeLayoutId];

/** The id set the account, Home and list surfaces all share today (`cdr | gs`); the intersection keeps a surface that grows its own layout from silently compiling here. */
type PageLayoutId = FlowAccountLayoutId & FlowHomeLayoutId & FlowListLayoutId;

const PAGE_LAYOUT_IDS: readonly PageLayoutId[] = FlowAccountLayoutIdSchema.options;

interface SlotLayoutInput<Id extends FlowListLayoutId, Spec> {
  slot: FlowLayoutSlot;
  /** Spec per id; must cover every id in `available`. */
  registry: Record<Id, Spec>;
  options: Spec[];
  available: readonly Id[];
  saved: Id | null;
  /** Lists only: the open saved view's pin. */
  viewPin?: Id | null;
  /** Lists only: the User Role's per-table override. */
  uiViewOverrideOf?: (team: FlowRole) => Id | null;
  uiViewDefaultOf: (team: FlowRole) => Id;
  fallback: Id;
}

/** The one body behind the account, Home and list hooks. */
function useFlowSlotLayout<Id extends FlowListLayoutId, Spec>({
  slot,
  registry,
  options,
  available,
  saved,
  viewPin = null,
  uiViewOverrideOf,
  uiViewDefaultOf,
  fallback,
}: SlotLayoutInput<Id, Spec>): FlowLayoutResult<Id, Spec> {
  const { team, isLoaded } = useRoleView();
  const { setFlowLayoutChoice } = useLayoutPrefs();
  const storedPick = useWorkingFlowLayout(slot);
  // The store holds any list id; this surface only honours one it offers.
  const explicit = available.find(id => id === storedPick) ?? null;
  const resolveWith = (workingPick: Id | null) =>
    resolveFlowLayout<Id>({
      working: workingPick,
      saved,
      viewPin,
      uiViewOverride: team && uiViewOverrideOf ? uiViewOverrideOf(team) : null,
      uiViewDefault: team ? uiViewDefaultOf(team) : null,
      available,
      fallback,
    });
  // What shows without a working pick. A stored pick equal to it is inert (a
  // pick kept across a pin-writing save, see `pick`): it is not Modified.
  const baselineId = resolveWith(null).id;
  const working = explicit === baselineId ? null : explicit;
  const { id: resolvedId, source } = resolveWith(working);
  const sourceLabel = flowLayoutSourceLabel(source, team);

  const pick = useCallback(
    (id: Id) => {
      if (id === resolvedId) return;
      // Picking the baseline normally clears the working pick rather than
      // storing one that reads "Modified" for nothing. While a pin-writing save
      // is in flight the baseline is about to move (the response installs the
      // view's new pin), so the pick is kept explicit to survive it.
      const keepExplicit = flowLayoutPinWriteInFlight(slot);
      setWorkingFlowLayout(slot, id === baselineId && !keepExplicit ? null : id);
    },
    [resolvedId, baselineId, slot],
  );

  const setAsDefault = useCallback(async (): Promise<boolean> => {
    // The pick and its version, both read from the live store in one go, never
    // a render-closure pick paired with a live version: the rep may pick again
    // while the write is in flight, and the version tells.
    const { bySlot, versionBySlot } = useFlowLayoutWorkingStore.getState();
    const pickToSet = bySlot[slot] ?? null;
    const version = versionBySlot[slot] ?? 0;
    if (pickToSet === null || pickToSet === baselineId) return false;
    try {
      await setFlowLayoutChoice(slot, pickToSet);
    } catch {
      // onError rolled the cache back and toasted; the pick stays Modified.
      return false;
    }
    if (workingFlowLayoutVersion(slot) === version) setWorkingFlowLayout(slot, null);
    return true;
  }, [slot, baselineId, setFlowLayoutChoice]);

  const reset = useCallback(() => {
    // An inert baseline-equal entry is dropped too, and does not count as the
    // discarded pick: the same click then clears the saved default.
    if (explicit !== null) setWorkingFlowLayout(slot, null);
    if (working !== null) return;
    if (saved === null) return;
    setFlowLayoutChoice(slot, null).catch(noop);
  }, [explicit, working, saved, slot, setFlowLayoutChoice]);

  return {
    id: resolvedId,
    source,
    sourceLabel,
    spec: registry[resolvedId],
    options,
    isLoaded,
    hasChoice: working !== null || saved !== null,
    baselineId,
    isModified: source === 'working',
    pick,
    setAsDefault,
    reset,
  };
}

const ACCOUNT_OPTIONS: AccountSpec[] = Object.values<AccountSpec>(ACCOUNT_LAYOUTS);
const HOME_OPTIONS: HomeSpec[] = Object.values<HomeSpec>(HOME_LAYOUTS);

/** The account page's layout (tabs and default tab). */
export function useFlowAccountLayout(): FlowLayoutResult<FlowAccountLayoutId, AccountSpec> {
  const { flowLayoutAccount } = useLayoutPrefs();
  return useFlowSlotLayout<PageLayoutId, AccountSpec>({
    slot: 'account',
    registry: ACCOUNT_LAYOUTS,
    options: ACCOUNT_OPTIONS,
    available: PAGE_LAYOUT_IDS,
    saved: flowLayoutAccount,
    uiViewDefaultOf: team => UI_VIEW_FLOW_LAYOUTS[team].defaults.account,
    fallback: FLOW_LAYOUT_FALLBACK_ID,
  });
}

/** Home's layout (columns, card facts, deal brief, loop links). */
export function useFlowHomeLayout(): FlowLayoutResult<FlowHomeLayoutId, HomeSpec> {
  const { flowLayoutHome } = useLayoutPrefs();
  return useFlowSlotLayout<PageLayoutId, HomeSpec>({
    slot: 'home',
    registry: HOME_LAYOUTS,
    options: HOME_OPTIONS,
    available: PAGE_LAYOUT_IDS,
    saved: flowLayoutHome,
    uiViewDefaultOf: team => UI_VIEW_FLOW_LAYOUTS[team].defaults.home,
    fallback: FLOW_LAYOUT_FALLBACK_ID,
  });
}

export type FlowListLayoutResult = FlowLayoutResult<FlowListLayoutId, FlowListLayoutSpec>;

export interface FlowListLayoutOptions {
  /** The open saved view's pinned layout (`FlowView.flowLayout`), if any. */
  viewPin?: FlowListLayoutId | null;
}

/**
 * A Flow table's list layout (labels, alignment, cells, density, widths).
 * Every layout is offered to everyone; the table's own "Flow default" is
 * `FLOW_LAYOUT_TABLE_FALLBACK_ID[table]` (Signal Response for Signals, CDR otherwise).
 */
export function useFlowListLayout(table: FlowLayoutTable, { viewPin = null }: FlowListLayoutOptions = {}): FlowListLayoutResult {
  const { flowLayoutList } = useLayoutPrefs();
  const options = useMemo(() => listFlowLayoutsFor(table), [table]);
  const available = useMemo(() => options.map(spec => spec.id), [options]);
  return useFlowSlotLayout<FlowListLayoutId, FlowListLayoutSpec>({
    slot: table,
    registry: LIST_LAYOUTS,
    options,
    available,
    saved: flowLayoutList?.[table] ?? null,
    viewPin,
    uiViewOverrideOf: team => UI_VIEW_FLOW_LAYOUTS[team].listByTable?.[table] ?? null,
    uiViewDefaultOf: team => UI_VIEW_FLOW_LAYOUTS[team].defaults.list,
    fallback: FLOW_LAYOUT_TABLE_FALLBACK_ID[table],
  });
}
